// Player settings, kept in localStorage.
//
// Deliberately a plain typed record with defaults, read through one accessor:
// a setting that is read directly from storage is a setting that can arrive as
// a string, or as undefined on a machine that has never seen it, and every
// caller then grows its own fallback. `load` is the only place that decides
// what a missing or malformed value means.
//
// Audio is here before there is any audio. The volumes cost nothing to store,
// and having the panel and the persistence in place means adding sound later is
// wiring a player to a number that already exists.

const KEY = 'riftbound.settings.v1';

export interface Settings {
  /** 0..1. Nothing reads these yet; the mixer will. */
  musicVolume: number;
  sfxVolume: number;
  muted: boolean;
  /**
   * Milliseconds the AI pauses before each action, so its turn is followable.
   * 0 means "as fast as the machine can", which is also what the test harness
   * uses -- and at 0 the scheduler stops using a timer at all.
   */
  aiDelayMs: number;
  /** 318. The idle flex on a card in hand. Off for anyone who finds it busy. */
  cardMotion: boolean;
  /**
   * 164.2. Whether runes may be tapped by hand.
   *
   * Off by default, and that is the point: exhausting and recycling both "add
   * resources", but a recycle is gone for the game, and one stray click spent
   * a rune permanently on a cost that never needed it. Costs are paid by the
   * payment search instead.
   *
   * Kept as an option because it is not *only* a footgun: a `payTo` inside a
   * resolution spends whatever is already floating and never stops to ask, so
   * pre-tapping is the only way to make one of those succeed.
   */
  manualRunes: boolean;
  /** Whether the debug log panel is open. */
  showLog: boolean;
  /** Include the engine's own event stream under each action in the log. */
  logEffects: boolean;
  /**
   * How hard the AI plays. Chosen on the deck screen and remembered, because
   * it is a preference about the game rather than about one match.
   *
   * `?ai=` still overrides it, which is what the arena and the harnesses use.
   */
  aiDifficulty: 'easy' | 'normal' | 'hard';
}

export const DEFAULTS: Settings = {
  musicVolume: 0.5,
  sfxVolume: 0.7,
  muted: false,
  aiDelayMs: 550,
  cardMotion: true,
  manualRunes: false,
  showLog: false,
  logEffects: true,
  aiDifficulty: 'normal',
};

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/** Reads stored settings, falling back per-field rather than all-or-nothing. */
export function load(): Settings {
  let stored: Partial<Record<keyof Settings, unknown>> = {};
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) stored = JSON.parse(raw) as Partial<Record<keyof Settings, unknown>>;
  } catch {
    // Private windows, cleared site data, or a browser refusing storage: the
    // defaults are a complete answer, so this is not worth surfacing.
  }
  const num = (v: unknown, fallback: number): number =>
    (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
  const bool = (v: unknown, fallback: boolean): boolean =>
    (typeof v === 'boolean' ? v : fallback);

  return {
    musicVolume: clamp01(num(stored.musicVolume, DEFAULTS.musicVolume)),
    sfxVolume: clamp01(num(stored.sfxVolume, DEFAULTS.sfxVolume)),
    muted: bool(stored.muted, DEFAULTS.muted),
    // Clamped to something a person can actually sit through.
    aiDelayMs: Math.max(0, Math.min(3000, num(stored.aiDelayMs, DEFAULTS.aiDelayMs))),
    cardMotion: bool(stored.cardMotion, DEFAULTS.cardMotion),
    manualRunes: bool(stored.manualRunes, DEFAULTS.manualRunes),
    showLog: bool(stored.showLog, DEFAULTS.showLog),
    logEffects: bool(stored.logEffects, DEFAULTS.logEffects),
    // Checked against the three that exist rather than cast: a stored value
    // from an older build, or a hand-edited one, must not become a difficulty
    // the weight table has no row for.
    aiDifficulty: stored.aiDifficulty === 'easy' || stored.aiDifficulty === 'hard'
      || stored.aiDifficulty === 'normal'
      ? stored.aiDifficulty
      : DEFAULTS.aiDifficulty,
  };
}

export function save(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Not being able to remember a preference is not a reason to fail a match.
  }
}
